import { UserMemberListFilter } from "@gymflow/api";
import {
  ApiTokenListResponse,
  CalculateChangeSubscriptionPriceNewRequestBody,
  CalculateChangeSubscriptionPriceNewResponseBody,
  CalculateScheduleDates,
  DetailedInvoiceDTO,
  InvoiceNewDTO,
  InvoiceStatus,
  LeadDTO,
  MembershipBean,
  MembershipType,
  PaymentMethodDTO,
  PaymentMethodSetupIntent,
  SubscriptionPromotion,
  SubscriptionPromotionSummary,
  SubscriptionSessionPackDTO,
  SubscriptionSessionPackUsageHistoryDTO,
  TokenPageableRequestParams,
  UserMemberBean,
  UserMemberListItem,
  UserMemberPost,
  UserMemberProfileDTO,
  UserMemberProfileDTOPatch,
  UserMemberSearchByFullNameResult,
  UserMemberSignupAsStaffDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";
import QueryString from "qs";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";
import { SortType } from "./features/types";

export type UserMemberApiType = ReturnType<typeof userMemberApi>;

const userMemberApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/` as const;
  const fieldsToTrim = [
    "firstName",
    "lastName",
    "mobileNumber",
    "dateBirth",
    "postCode",
    "addressLine1",
    "addressLine2",
    "city",
    "email",
    "emergencyContact",
    "emergencyContactName",
    "username",
  ];

  return {
    findById(id: string) {
      return axiosInstance.get<UserMemberBean>(
        `${clubPrefix}user/member/${id}`,
      );
    },
    findByFullName(term: string, { page = 0, limit = 10 }) {
      return axiosInstance.get<UserMemberSearchByFullNameResult[]>(
        `${clubPrefix}catalog/user/search`,
        {
          params: { q: term, page, size: limit },
        },
      );
    },
    globalSearch(term: string, size = 5) {
      return axiosInstance.get<(UserMemberBean | LeadDTO)[]>(
        `${clubPrefix}catalog/global/search`,
        {
          params: { q: term, size },
        },
      );
    },
    create(newResource: UserMemberPost) {
      const trimmedData = trimFields(newResource, fieldsToTrim);

      return axiosInstance.post(`${clubPrefix}user/member/full`, trimmedData);
    },
    membershipOverdue({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: PaginationParams & {
      extraParams?: any;
      sort?: SortType;
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-overdue`,
        config,
      );
    },
    membershipPaused({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: PaginationParams & {
      extraParams?: any;
      sort?: SortType;
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-paused`,
        config,
      );
    },
    calculateProRata(
      memberId: string,
      subscriptionId: number,
      resumeDate: string,
    ) {
      return axiosInstance.get(
        `${clubPrefix}user/member/${memberId}/calculate-prorata-resume-subscription/${subscriptionId}`,
        { params: { resumeDate } },
      );
    },
    resumeSubscriptionEarly(
      memberId: string,
      subscriptionId: number,
      resumeDate: string,
      isWaived: boolean,
      paymentMethodId: string | undefined,
    ) {
      const body = { isWaived, earlyPauseEndDate: resumeDate } as any;
      if (!isWaived) {
        body.paymentMethodId = paymentMethodId;
      }
      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/membership/resume-early/${subscriptionId}`,
        body,
      );
    },
    membershipEnding({
      dateFrom,
      dateTo,
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
      membershipIncluded,
      membershipExcluded,
      membershipType,
    }: MembershipReportParams) {
      const config = {
        params: {
          dateFrom,
          dateTo,
          page,
          size: limit,
          membershipIncluded,
          membershipExcluded,
          membershipType,
          sort: ["subscriptionId"],
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort.push(createSortParam(sort));
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-ending`,
        config,
      );
    },
    membershipEndingCsv(filters: MembershipReportCsvParams) {
      return axiosInstance.get(
        `${clubPrefix}user/member/membership-ending/csv`,
        {
          params: filters,
        },
      );
    },
    membershipResuming({
      dateFrom,
      dateTo,
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
      membershipIncluded,
      membershipExcluded,
      membershipType,
    }: MembershipReportParams) {
      const config = {
        params: {
          dateFrom,
          dateTo,
          page,
          size: limit,
          membershipIncluded,
          membershipExcluded,
          membershipType,
          sort: ["subscriptionId"],
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort.push(createSortParam(sort));
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-resuming`,
        config,
      );
    },
    membershipResumingCsv(filters: MembershipReportCsvParams) {
      return axiosInstance.get(
        `${clubPrefix}user/member/membership-resuming/csv`,
        {
          params: filters,
        },
      );
    },
    membershipPausing({
      dateFrom,
      dateTo,
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
      membershipIncluded,
      membershipExcluded,
      membershipType,
    }: MembershipReportParams) {
      const config = {
        params: {
          dateFrom,
          dateTo,
          page,
          size: limit,
          membershipIncluded,
          membershipExcluded,
          membershipType,
          sort: ["subscriptionId"],
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort.push(createSortParam(sort));
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-pausing`,
        config,
      );
    },
    membershipPausingCsv(filters: MembershipReportCsvParams) {
      return axiosInstance.get(
        `${clubPrefix}user/member/membership-pausing/csv`,
        {
          params: filters,
        },
      );
    },
    membershipPending({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: PaginationParams & {
      extraParams?: any;
      sort?: SortType;
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-pending`,
        config,
      );
    },
    eventAttendance({
      startDate,
      endDate,
      gt,
      lt,
      eq,
      membership,
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: {
      startDate: string;
      endDate: string;
      gt?: number;
      lt?: number;
      eq?: number;
      membership?: number[];
    } & PaginationParams & {
        extraParams?: any;
        sort?: SortType;
      }) {
      const config = {
        params: {
          dateFrom: startDate,
          dateTo: endDate,
          gt,
          lt,
          eq,
          membership,
          page,
          size: limit,
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}event/occurrence/rsvp/attendance`,
        config,
      );
    },
    eventAttendanceCsv(filters: {
      dateFrom: string;
      dateTo: string;
      gt?: number;
      lt?: number;
      eq?: number;
      membership?: number[];
    }) {
      return axiosInstance.get(
        `${clubPrefix}event/occurrence/rsvp/attendance/csv`,
        {
          params: filters,
        },
      );
    },
    classesRun({
      startDate,
      endDate,
      activityId,
      eventHostId,
      page = 0,
      limit = 10,
      sort,
    }: {
      startDate?: string;
      endDate?: string;
      activityId?: number[];
      eventHostId?: string[];
    } & PaginationParams & {
        sort?: SortType[];
        extraParams?: any;
      }) {
      const config = {
        params: {
          dateFrom: startDate,
          dateTo: endDate,
          activityId,
          eventHost: eventHostId,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        const parsedSort = sort.reduce((acc, v) => {
          acc.push(createSortParam(v));
          return acc;
        }, [] as string[]);
        config.params.sort = parsedSort;
      }

      return axiosInstance.get(
        `${clubPrefix}event/occurrence/classes-run`,
        config,
      );
    },
    classesRunCsv(filters: {
      dateFrom?: string;
      dateTo?: string;
      activityId?: number[];
      eventHost?: string[];
    }) {
      return axiosInstance.get(
        `${clubPrefix}event/occurrence/classes-run/csv`,
        {
          params: filters,
        },
      );
    },
    noShows({
      dateFrom,
      dateTo,
      page = 0,
      limit = 10,
      sort,
    }: {
      dateFrom?: string;
      dateTo?: string;
    } & PaginationParams & {
        sort?: SortType[];
      }) {
      const config = {
        params: {
          dateFrom,
          dateTo,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        const parsedSort = sort.reduce((acc, v) => {
          acc.push(createSortParam(v));
          return acc;
        }, [] as string[]);
        config.params.sort = parsedSort;
      }

      return axiosInstance.get(
        `${clubPrefix}event/occurrence/no-shows`,
        config,
      );
    },
    noShowsCsv(filters: { dateFrom?: string; dateTo?: string }) {
      return axiosInstance.get(`${clubPrefix}event/occurrence/no-shows/csv`, {
        params: filters,
      });
    },
    lateCancellations({
      startDate,
      endDate,
      page = 0,
      limit = 10,
      sort,
    }: {
      startDate?: string;
      endDate?: string;
    } & PaginationParams & {
        sort?: SortType[];
      }) {
      const config = {
        params: {
          dateFrom: startDate,
          dateTo: endDate,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        const parsedSort = sort.reduce((acc, v) => {
          acc.push(createSortParam(v));
          return acc;
        }, [] as string[]);
        config.params.sort = parsedSort;
      }

      return axiosInstance.get(
        `${clubPrefix}event/occurrence/late-cancellations`,
        config,
      );
    },
    lateCancellationsCsv(filters: { dateFrom?: string; dateTo?: string }) {
      return axiosInstance.get(
        `${clubPrefix}event/occurrence/late-cancellations/csv`,
        {
          params: filters,
        },
      );
    },
    memberStatusList({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: PaginationParams & {
      extraParams?: any;
      sort?: SortType;
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      };
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/member-status`,
        config,
      );
    },
    memberStatusListCsv(filters: any) {
      return axiosInstance.get(`${clubPrefix}user/member/member-status/csv`, {
        params: filters,
      });
    },
    async fetchPaymentMethods(memberId: string, clubId: number) {
      const result = await axiosInstance.get<PaymentMethodDTO[]>(
        `${clubPrefix}payment-method/${memberId}`,
      );
      (result as any).data = (result as any).data.map((e: any) => ({
        ...e,
        id: e.paymentMethodId,
      }));
      return result;
    },
    async getPaymentMethod(memberId: string, paymentMethodId: string) {
      return await axiosInstance.get<PaymentMethodDTO>(
        `${clubPrefix}payment-method/${memberId}/${paymentMethodId}`,
      );
    },
    removePaymentMethod(
      memberId: string,
      clubId: number,
      paymentMethodId: string,
    ) {
      return axiosInstance.delete(
        `${clubPrefix}payment-method/${memberId}/${paymentMethodId}`,
      );
    },
    assignDefaultPaymentMethod(
      memberId: string,
      clubId: number,
      paymentMethodId: string,
    ) {
      return axiosInstance.put(
        `${clubPrefix}payment-method/${memberId}/default/${paymentMethodId}`,
      );
    },
    expireSubscription(
      memberId: string,
      subscriptionId: number,
      expiredDate: string | undefined,
    ) {
      const body = expiredDate ? { expirationDateTime: expiredDate } : {};

      return axiosInstance.patch(
        `${clubPrefix}user/member/${memberId}/membership/expire/${subscriptionId}`,
        body,
      );
    },
    pauseSubscription(
      memberId: string,
      subscriptionId: number,
      immediately: boolean,
      pauseDate: string | undefined,
      resumeDate: string,
    ) {
      const body = {
        pauseEndDate: resumeDate,
      } as any;

      if (immediately) {
        body.effectiveImmediately = immediately;
      } else {
        body.pauseStartDate = pauseDate;
      }

      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/membership/pause/${subscriptionId}`,
        body,
      );
    },
    cancelPauseSubscription(memberId: string, subscriptionId: number) {
      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/membership/unschedule-pause/${subscriptionId}`,
      );
    },
    cancelSubscription({
      userMemberId,
      subscriptionId,
      effectiveImmediately,
      cancellationDate,
      cancellationReasonId,
    }: {
      userMemberId: string;
      subscriptionId: number;
      effectiveImmediately?: boolean;
      cancellationDate?: string;
      cancellationReasonId: number;
    }) {
      return axiosInstance.post(
        `${clubPrefix}user/member/${userMemberId}/membership/cancel/${subscriptionId}`,
        {
          effectiveImmediately,
          cancellationDate,
          cancellationReasonId,
        },
      );
    },
    updatePendingDate(
      memberId: string,
      subscriptionId: number,
      immediately: boolean,
      when: string | undefined,
    ) {
      const body = {} as any;
      if (immediately) {
        body.startSubscriptionEffectiveImmediately = true;
      } else if (when) {
        body.startSubscriptionEffectiveImmediately = false;
        body.pendingStartDate = when;
      }

      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/membership/update-pending-date/${subscriptionId}`,
        body,
      );
    },
    revokeCancellation(memberId: string, subscriptionId: number) {
      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/membership/revoke-cancellation/${subscriptionId}`,
      );
    },
    calculateChangeSubscriptionPrice({
      subscriptionId,
      membershipId,
    }: CalculateChangeSubscriptionPriceNewRequestBody) {
      return axiosInstance.get<CalculateChangeSubscriptionPriceNewResponseBody>(
        `${clubPrefix}subscription/${subscriptionId}/change/price?membershipId=${membershipId}`,
      );
    },
    changeSubscriptionPrice(
      clubId: number,
      memberId: string,
      subscriptionId: number,
      newSubscriptionPrice: number,
    ) {
      return axiosInstance.patch(
        `${clubPrefix}user/member/${memberId}/change-subscription-price`,
        { subscriptionId, newSubscriptionPrice },
      );
    },
    highlightedInvoice({ memberId }: { memberId: string }) {
      return axiosInstance.get<DetailedInvoiceDTO>(
        `${clubPrefix}user-member-invoice-list/${memberId}/highlighted`,
      );
    },
    findInvoicesNew({
      memberId,
      ...params
    }: {
      memberId: string;
    } & TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<InvoiceNewDTO>>(
        `${clubPrefix}user-member-invoice-list/${memberId}`,
        { params },
      );
    },
    adjustInvoice(invoiceNumber: string, amount: number) {
      return axiosInstance.put<{}>(
        `${clubPrefix}invoice/${invoiceNumber}/adjust`,
        {
          newDueAmount: amount,
        },
      );
    },
    getInvoiceByInvoiceNumber(invoiceNumber: string) {
      const url = `${clubPrefix}invoice/${invoiceNumber}`;
      return axiosInstance.get<DetailedInvoiceDTO>(url);
    },
    collectPastDueInvoice(invoiceNumber: string, paymentMethodId: string) {
      const url = `${clubPrefix}invoice/${invoiceNumber}/collect-past-due/${paymentMethodId}`;
      return axiosInstance.put(url);
    },
    markCollectedByCustomPayment(
      invoiceNumber: string,
      customPaymentCategoryId: number,
    ) {
      const url = `${clubPrefix}invoice/${invoiceNumber}/mark-custom-payment-collected/${customPaymentCategoryId}`;
      return axiosInstance.put(url);
    },
    refundInvoice(invoiceNumber: string) {
      const url = `${clubPrefix}invoice/${invoiceNumber}/refund/`;
      return axiosInstance.put(url);
    },
    writeOffInvoice(invoiceNumber: string) {
      const url = `${clubPrefix}invoice/${invoiceNumber}/write-off/`;
      return axiosInstance.put(url);
    },
    cancelInvoicePaymentAuth(invoiceNumber: string) {
      const url = `${clubPrefix}invoice/${invoiceNumber}/payment-auth-cancel/`;
      return axiosInstance.put(url);
    },
    invoiceFile(invoiceNumber: string) {
      return axiosInstance.get(`${clubPrefix}invoice/${invoiceNumber}/pdf`, {
        responseType: "blob",
      });
    },
    changeBillingDate(clubId: number, subscriptionId: number, newDate: string) {
      return axiosInstance.patch(
        `${clubPrefix}user/member/change-subscription-billing-date/${subscriptionId}`,
        {
          changeDate: newDate,
        },
      );
    },
    changeContractEndDate(subscriptionId: number, newDate: string) {
      return axiosInstance.patch(
        `${clubPrefix}user/member/change-subscription-contract-date/${subscriptionId}`,
        { changeDate: newDate },
      );
    },
    membershipSold({
      dateFrom,
      dateTo,
      membershipIncluded,
      membershipExcluded,
      page = 0,
      limit = 10,
      sort,
    }: MembershipReportParams) {
      const config = {
        params: {
          dateFrom,
          dateTo,
          membershipIncluded,
          membershipExcluded,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get(
        `${clubPrefix}user/member/membership-sold`,
        config,
      );
    },
    membershipSoldCsv(filters: MembershipReportCsvParams) {
      return axiosInstance.get(`${clubPrefix}user/member/membership-sold/csv`, {
        params: filters,
      });
    },

    membershipLinkMember(
      primarySubscriptionId: number,
      secondaryMemberId: string,
    ) {
      const url = `${clubPrefix}user/member/${secondaryMemberId}/link-subscription/${primarySubscriptionId}`;
      return axiosInstance.put(url);
    },
    listMembershipLinkedMembers(primarySubscriptionId: number) {
      const url = `${clubPrefix}user/member/membership/${primarySubscriptionId}/linked-members`;
      return axiosInstance.get(url);
    },
    listNew(params: UserMemberListFilter & TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<UserMemberListItem>>(
        `${clubPrefix}user-member-list`,
        { params },
      );
    },
    listNewCount(params: UserMemberListFilter) {
      return axiosInstance.get<{ count: number }>(
        `${clubPrefix}user-member-list/count`,
        {
          params,
        },
      );
    },
    listNewCsv(params: UserMemberListFilter) {
      return axiosInstance.get(`${clubPrefix}user-member-list/csv`, {
        params,
        headers: {
          "Content-Disposition": "attachment; filename=users.csv",
        },
      });
    },
    addPromotionCodeToSubscription({
      subscriptionId,
      promotionCode,
    }: {
      subscriptionId: number;
      promotionCode: string;
    }) {
      return axiosInstance.put<SubscriptionPromotion>(
        `${clubPrefix}subscription/${subscriptionId}/promotion`,
        {
          promotionCode,
        },
      );
    },
    addPromotionCodeToSubscriptionSummary({
      subscriptionId,
      promotionCode,
    }: {
      subscriptionId: number;
      promotionCode: string;
    }) {
      return axiosInstance.get<SubscriptionPromotionSummary>(
        `${clubPrefix}subscription/${subscriptionId}/promotion`,
        {
          params: { promotionCode },
        },
      );
    },
    removePromotionCodeFromSubscription({
      subscriptionId,
    }: {
      subscriptionId: number;
    }) {
      return axiosInstance.delete(
        `${clubPrefix}subscription/${subscriptionId}/promotion`,
      );
    },
    changeSubscriptionPaymentProRata({
      subscriptionId,
      newMembershipId,
    }: {
      subscriptionId: number;
      newMembershipId: number;
    }) {
      return axiosInstance.post<{
        invoiceNumber: string;
        invoiceStatus: InvoiceStatus;
      }>(
        `${clubPrefix}subscription/${subscriptionId}/change/payment-pro-rata`,
        {
          membershipId: newMembershipId,
        },
      );
    },
    changeSubscriptionPaymentWaived({
      subscriptionId,
      newMembershipId,
    }: {
      subscriptionId: number;
      newMembershipId: number;
    }) {
      return axiosInstance.post(
        `${clubPrefix}subscription/${subscriptionId}/change/payment-waived`,
        {
          membershipId: newMembershipId,
        },
      );
    },
    changeSubscriptionSchedule({
      subscriptionId,
      newMembershipId,
      changeDate,
    }: {
      subscriptionId: number;
      newMembershipId: number;
      changeDate: string;
    }) {
      return axiosInstance.post(
        `${clubPrefix}subscription/${subscriptionId}/change/schedule`,
        {
          membershipId: newMembershipId,
          changeDate,
        },
      );
    },
    changeSubscriptionUnschedule({
      subscriptionId,
    }: {
      subscriptionId: number;
    }) {
      return axiosInstance.delete(
        `${clubPrefix}subscription/${subscriptionId}/change/unschedule`,
      );
    },
    calculateScheduleDates(subscriptionId: number) {
      return axiosInstance.get<CalculateScheduleDates>(
        `${clubPrefix}subscription/${subscriptionId}/change/calculate-schedule-dates`,
      );
    },
    attachAuthorizedSetupIntent(memberId: string, setupIntentId: string) {
      return axiosInstance.put<PaymentMethodDTO>(
        `${clubPrefix}payment-method/${memberId}/setup-intent-authorized/${setupIntentId}`,
      );
    },
    getPaymentMethodSetupIntent(memberId: string) {
      return axiosInstance.put<PaymentMethodSetupIntent>(
        `${clubPrefix}payment-method/${memberId}/setup-intent`,
      );
    },
    changeMembershipOptions({
      subscriptionId,
      nextPageToken,
    }: {
      subscriptionId: number;
      nextPageToken?: string;
    }) {
      return axiosInstance.get<ApiTokenListResponse<MembershipBean>>(
        `${clubPrefix}subscription/${subscriptionId}/change/available-memberships?${QueryString.stringify(
          { nextPageToken, limit: 10 },
        )}`,
      );
    },
    accountDelete(memberId: string, clubId: number) {
      return axiosInstance.post(
        `${clubPrefix}user/member/${memberId}/account-delete`,
        {
          clubId,
        },
      );
    },
    userMemberSessionPackSubscriptionCurrent({
      userMemberId,
      nextPageToken,
    }: {
      userMemberId: string;
      nextPageToken?: string;
    }) {
      return axiosInstance.get<
        ApiTokenListResponse<SubscriptionSessionPackDTO>
      >(
        `${clubPrefix}user-member/${userMemberId}/session-pack-subscription/current?${QueryString.stringify(
          { nextPageToken, limit: 20 },
        )}`,
      );
    },
    userMemberSessionPackSubscriptionPrevious({
      userMemberId,
      nextPageToken,
    }: {
      userMemberId: string;
      nextPageToken?: string;
    }) {
      return axiosInstance.get<
        ApiTokenListResponse<SubscriptionSessionPackDTO>
      >(
        `${clubPrefix}user-member/${userMemberId}/session-pack-subscription/previous?${QueryString.stringify(
          { nextPageToken, limit: 20 },
        )}`,
      );
    },
    userMemberSessionPackSubscriptionUsageHistory({
      subscriptionId,
      nextPageToken,
    }: {
      subscriptionId: number;
      nextPageToken?: string;
    }) {
      return axiosInstance.get<
        ApiTokenListResponse<SubscriptionSessionPackUsageHistoryDTO>
      >(
        `${clubPrefix}session-pack-subscription/${subscriptionId}/usage-history?${QueryString.stringify(
          { nextPageToken, limit: 20 },
        )}`,
      );
    },
    deleteSessionPackSubscriptionBySubscriptionId(subscriptionId: number) {
      return axiosInstance.delete(
        `${clubPrefix}session-pack-subscription/${subscriptionId}`,
      );
    },
    sessionPackSubscriptionSessionsLeft({
      subscriptionId,
      quantity,
    }: {
      subscriptionId: number;
      quantity: number;
    }) {
      return axiosInstance.put(
        `${clubPrefix}session-pack-subscription/${subscriptionId}/sessions-left`,
        { quantity },
      );
    },
    sessionPackSubscriptionActivate({
      subscriptionId,
      expiryDate,
    }: {
      subscriptionId: number;
      expiryDate: string;
    }) {
      return axiosInstance.put(
        `${clubPrefix}session-pack-subscription/${subscriptionId}/activate`,
        { expiryDate },
      );
    },
    sessionPackSubscriptionPendingStartDate({
      subscriptionId,
      startDate,
    }: {
      subscriptionId: number;
      startDate: string;
    }) {
      return axiosInstance.put(
        `${clubPrefix}session-pack-subscription/${subscriptionId}/start-date`,
        { startDate },
      );
    },
    sessionPackSubscriptionExpiryDate({
      subscriptionId,
      expiryDate,
    }: {
      subscriptionId: number;
      expiryDate: string;
    }) {
      return axiosInstance.put(
        `${clubPrefix}session-pack-subscription/${subscriptionId}/expiry-date`,
        { expiryDate },
      );
    },
    sendAchVerificationEmail({
      memberId,
      paymentMethodId,
    }: {
      memberId: string;
      paymentMethodId: string;
    }) {
      return axiosInstance.put(
        `${clubPrefix}payment-method/${memberId}/${paymentMethodId}/send-verify-email`,
      );
    },
    createUser(values: UserMemberSignupAsStaffDTO) {
      return axiosInstance.post<{
        userMemberId: string;
      }>(`${clubPrefix}user-member`, values);
    },
    getMemberProfile(memberId: string) {
      return axiosInstance.get<UserMemberProfileDTO>(
        `${clubPrefix}user-member-profile/${memberId}`,
      );
    },
    updateMemberProfile(
      memberId: string,
      data: Partial<UserMemberProfileDTOPatch>,
    ) {
      return axiosInstance.put(
        `${clubPrefix}user-member-profile/${memberId}`,
        data,
      );
    },
  };
};

export default userMemberApi;

export type PaginationParams = {
  page?: number;
  limit?: number;
};

export type MembershipReportCsvParams = {
  dateFrom: string;
  dateTo: string;
  membershipIncluded?: number[];
  membershipExcluded?: number[];
  membershipType?: MembershipType;
};

export type MembershipReportParams = MembershipReportCsvParams &
  PaginationParams & {
    extraParams?: any;
    sort?: SortType;
  };
